import type { EntityDocument } from '../types/entities';
import type { useWorkspaceStore } from '../stores/workspace';
import { type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import type { TiptapDoc } from '../types/entities';
import { api } from '../lib/api';

const chronisExtensionId = 'demlnoekfbpigmdmmbdcipinnmoalgef';

export function useAiCompanion(options: {
  store: ReturnType<typeof useWorkspaceStore>;
  draft: { title: string; summary: string; contentDoc: TiptapDoc; fieldValues: Record<string, unknown> };
  isVersionReadonly: Readonly<Ref<boolean>>;
  entityAiUrl: Readonly<Ref<string>>;
  saveState: Readonly<Ref<string>>;
  saveLabel: Readonly<Ref<string>>;
  aiAssistantOpen: Ref<boolean>;
  aiAssistantQuestion: Ref<string>;
  aiAssistantState: Ref<'idle' | 'sent' | 'error'>;
  aiAssistantError: Ref<string>;
  askPreviewOpen: Ref<boolean>;
  askInitialQuestion: Ref<string>;
  askInitialDirect: Ref<boolean>;
  askAutoSend: Ref<boolean>;
  askRequestKey: Ref<number>;
  askSelectionMenu: Ref<{ x: number; y: number; text: string } | null>;
  scheduleSave: () => void;
  flushSave: () => Promise<void>;
  selectEntity: (id: string) => Promise<void>;
  addDevMessage: (message: unknown) => void;
  openDevWindow: () => void;
}) {
  const { store, draft, isVersionReadonly, entityAiUrl, saveState, saveLabel, aiAssistantOpen, aiAssistantQuestion, aiAssistantState, aiAssistantError, askPreviewOpen, askInitialQuestion, askInitialDirect, askAutoSend, askRequestKey, askSelectionMenu, scheduleSave, flushSave, selectEntity, addDevMessage, openDevWindow } = options;
  const { activeEntity, activeTemplate, entities } = storeToRefs(store);
function setAiUrl(value: string) {
  if (isVersionReadonly.value) return;
  // Alte Entities können den früheren camelCase-Schlüssel enthalten. Beim
  // nächsten Speichern wird er serverseitig einheitlich zu ai_url normalisiert.
  draft.fieldValues.ai_url = value;
  delete draft.fieldValues.aiUrl;
  scheduleSave();
}
async function saveDiscoveredAiUrl(event: Event) {
  const url = String((event as CustomEvent<{ url?: string }>).detail?.url ?? '').trim();
  if (!url || !activeEntity.value) return;
  addDevMessage(`Eigene KI-Chat-URL empfangen. Speichere sie an der Entity …`);
  setAiUrl(url);
  await flushSave();
  addDevMessage(saveState.value === 'saved' ? 'KI-Chat-URL wurde gespeichert.' : `KI-Chat-URL konnte nicht bestätigt werden: ${saveLabel.value}.`);
}
async function openChatGptSidePanel() {
  if (!activeEntity.value) return;
  openDevWindow();
  addDevMessage(`KI öffnen angefordert: ${entityAiUrl.value || 'https://chatgpt.com/'}`);
  aiAssistantOpen.value = true;
  aiAssistantState.value = 'idle';
  aiAssistantError.value = '';
  // Nur ein an dieser Entity gespeicherter Link verhindert den Startchat.
  // Ein Template-Standard verweist lediglich auf das Write-Bubble-Projekt und
  // soll daher weiterhin den UUID-gebundenen neuen Chat anlegen.
  const storedAiUrl = String(draft.fieldValues.ai_url ?? draft.fieldValues.aiUrl ?? '').trim();
  const message = { type: 'chronis-open-chatgpt', entity: { id: activeEntity.value.id, title: activeEntity.value.title, template: activeTemplate.value?.name ?? 'Entity', aiUrl: storedAiUrl || undefined, context: await enrichedEntityTransferText() } };
  const runtime = (window as Window & { chrome?: { runtime?: { lastError?: unknown; sendMessage?: (extensionId: string, payload: unknown, callback: () => void) => void } } }).chrome?.runtime;
  if (runtime?.sendMessage) {
    runtime.sendMessage(chronisExtensionId, message, () => {
      if (runtime.lastError) {
        addDevMessage('Direkte Erweiterungs-Verbindung fehlgeschlagen; nutze Seitenbrücke als Fallback.');
        window.postMessage(message, location.origin);
      } else addDevMessage('Direkte Erweiterungs-Verbindung bestätigt.');
    });
  } else window.postMessage(message, location.origin);
}
function closeChatGptSidePanel() {
  addDevMessage('KI-Begleiter schließen und Chronis maximieren angefordert.');
  window.postMessage({ type: 'chronis-close-chatgpt' }, location.origin);
  aiAssistantOpen.value = false;
}
async function openAskPreviewEntity(entityId: string) {
  askPreviewOpen.value = false;
  await selectEntity(entityId);
}
function openAskPanel(question = '', direct = false, autoSend = false) {
  askInitialQuestion.value = question;
  askInitialDirect.value = direct;
  askAutoSend.value = autoSend;
  askRequestKey.value += 1;
  askPreviewOpen.value = true;
}
function openAskSelectionMenu(event: MouseEvent) {
  if (!event.altKey || event.defaultPrevented) return;
  const selectedText = window.getSelection()?.toString().replace(/\s+/g, ' ').trim() ?? '';
  if (selectedText.length < 2) return;
  event.preventDefault();
  askSelectionMenu.value = {
    text: selectedText.slice(0, 2000),
    x: Math.min(event.clientX, window.innerWidth - 245),
    y: Math.min(event.clientY, window.innerHeight - 118)
  };
}
function openAskFromSelection(direct: boolean) {
  const selectedText = askSelectionMenu.value?.text ?? '';
  askSelectionMenu.value = null;
  if (selectedText) openAskPanel(selectedText, direct, direct);
}
function closeAskSelectionMenu() { askSelectionMenu.value = null; }
function documentText(value: unknown): string {
  if (!value || typeof value !== 'object') return '';
  if (Array.isArray(value)) return value.map(documentText).filter(Boolean).join('\n');
  const node = value as { text?: unknown; content?: unknown };
  const own = typeof node.text === 'string' ? node.text : '';
  const children = documentText(node.content);
  return [own, children].filter(Boolean).join(own && children ? '\n' : '');
}
function transferReferenceIds(value: unknown, ids = new Set<string>()) {
  if (typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)) ids.add(value);
  else if (Array.isArray(value)) value.forEach((item) => transferReferenceIds(item, ids));
  else if (value && typeof value === 'object') Object.values(value as Record<string, unknown>).forEach((item) => transferReferenceIds(item, ids));
  return ids;
}
function transferValue(value: unknown): string {
  if (typeof value === 'string') {
    const entity = entities.value.find((item) => item.id === value);
    return entity ? `${entity.title} (UUID: ${entity.id})` : JSON.stringify(value);
  }
  if (Array.isArray(value)) return `[${value.map(transferValue).join(', ')}]`;
  return JSON.stringify(value);
}
async function transferReferenceContext(id: string) {
  const summary = entities.value.find((item) => item.id === id);
  if (!summary) return `- UUID: ${id}\n  Reference unavailable.`;
  try {
    const entity = await api<EntityDocument>(`/api/v1/entities/${id}`);
    const header = [`- UUID: ${entity.id}`, `  Name: ${entity.title}`, `  Template: ${entity.templateKey}`];
    const summaryText = entity.aiSummary || entity.summary || 'No summary available.';
    if (entity.templateCategory === 'szene') return [...header, `  Summary: ${summaryText}`].join('\n');
    return [...header, `  Content: ${documentText(entity.workingVersion.contentDoc) || '(empty)'}`].join('\n');
  } catch {
    return `- UUID: ${id}\n  Name: ${summary.title}\n  Content unavailable.`;
  }
}
async function enrichedEntityTransferText() {
  if (!activeEntity.value) return '';
  const labels = new Map((activeTemplate.value?.fields ?? []).map((field) => [field.key, field.label]));
  const entries = Object.entries(draft.fieldValues);
  const fields = entries.map(([key, value]) => `- ${labels.get(key) ?? key} (${key}): ${transferValue(value)}`).join('\n') || '- No additional fields';
  const ids = [...new Set(entries.flatMap(([, value]) => [...transferReferenceIds(value)]).filter((id) => id !== activeEntity.value?.id))];
  const references = ids.length ? await Promise.all(ids.map(transferReferenceContext)) : [];
  return ['Chronis Entity', `Name: ${draft.title}`, `Entity UUID: ${activeEntity.value.id}`, `Template: ${activeTemplate.value?.name ?? 'Entity'}`, '', 'Fields:', fields, ...(references.length ? ['', 'Referenced entities:', ...references] : []), '', 'Document content:', documentText(draft.contentDoc) || '(empty)'].join('\n');
}
async function sendToAi(includeContent: boolean) {
  aiAssistantError.value = '';
  const question = aiAssistantQuestion.value.trim();
  const prompt = includeContent ? [await enrichedEntityTransferText(), question ? `\nFrage:\n${question}` : ''].filter(Boolean).join('\n') : question;
  if (!prompt.trim()) { aiAssistantState.value = 'error'; aiAssistantError.value = 'Bitte gib eine Frage ein oder übertrage den Inhalt.'; return; }
  openDevWindow();
  addDevMessage(`KI-Senden: ${includeContent ? 'Dokumentinhalt + Frage' : 'nur Frage'} (${prompt.length} Zeichen).`);
  window.dispatchEvent(new CustomEvent('chronis-message-to-chatgpt', { detail: { action: 'send-prompt', prompt } }));
  aiAssistantState.value = 'sent';
}
function runAiDebug(action: 'find-chatbox' | 'send-chat' | 'read-answer' | 'minimize-window' | 'maximize-window' | 'inspect-window') {
  openDevWindow();
  addDevMessage(`KI-Diagnose angefordert: ${action}.`);
  window.dispatchEvent(new CustomEvent('chronis-extension-debug', { detail: { action } }));
}
function handleAiCompanionClosed() { aiAssistantOpen.value = false; addDevMessage('KI-Begleiter geschlossen – Chronis wurde maximiert.'); }
  return { setAiUrl, saveDiscoveredAiUrl, openChatGptSidePanel, closeChatGptSidePanel, openAskPreviewEntity, openAskPanel, openAskSelectionMenu, openAskFromSelection, closeAskSelectionMenu, sendToAi, runAiDebug, handleAiCompanionClosed };
}
